import React, { useRef, useEffect, useCallback } from 'react';
import PostCard from '@/components/community/PostCard';
import { IPost } from '@/lib/community';
import { Spinner } from '@/components/ui/spinner';
import { useUserPosts } from '@/hooks/useUserPosts';

interface IProps {
	onPostClick: (post: IPost) => void;
	userId: string;
}

export default function UserPostsList({ onPostClick, userId }: IProps) {
	const {
		posts,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
	} = useUserPosts(userId);

	const observerRef = useRef<HTMLDivElement | null>(null);

	const handleObserver = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const [entry] = entries;
			if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
				fetchNextPage();
			}
		},
		[fetchNextPage, hasNextPage, isFetchingNextPage]
	);

	useEffect(() => {
		if (!observerRef.current) return;
		const observer = new IntersectionObserver(handleObserver, {
			rootMargin: '400px 0px',
			threshold: 0.1,
		});
		observer.observe(observerRef.current);

		return () => {
			if (observerRef.current) observer.unobserve(observerRef.current);
		};
	}, [handleObserver, posts.length, hasNextPage, isFetchingNextPage]);

	return (
		<div>
			<div className="mt-4 flex flex-col gap-4">
				{isLoading
					? Array.from({ length: 3 }).map((_, i) => (
							<div
								key={i}
								className="h-44 bg-gray-200 animate-pulse rounded-xl"
							/>
						))
					: posts.length === 0 ? (
							<div className="text-center text-gray-400">Постов не найдено</div>
						) : (
							posts.map((post) => (
								<PostCard
									key={post.id}
									post={post}
									onClick={() => onPostClick(post)}
								/>
							))
						)}
			</div>
			<div ref={observerRef} id="infinite-scroll-trigger" className="h-4" />
			{isFetchingNextPage && (
				<div className="flex justify-center py-4">
					<Spinner className="h-8 w-8" />
				</div>
			)}
		</div>
	);
}
